import { create } from 'zustand';
import { Patient, PatientPhoto, PatientInsurance } from '../types';

interface PatientStore {
  patients: Patient[];
  searchQuery: string;
  filterType: 'all' | 'active' | 'inactive';
  editingPatient: Patient | null;
  selectedPatient: Patient | null; // Currently viewed patient (for AI context)

  // Actions
  setPatients: (patients: Patient[]) => void;
  addPatient: (patient: Patient) => void;
  updatePatient: (patient: Patient) => void;
  deletePatient: (patientId: string) => void;
  setSearchQuery: (query: string) => void;
  setFilterType: (type: 'all' | 'active' | 'inactive') => void;
  setEditingPatient: (patient: Patient | null) => void;
  setSelectedPatient: (patient: Patient | null) => void;
  
  // Photo management
  uploadPatientPhoto: (patientId: string, file: File) => Promise<void>;
  removePatientPhoto: (patientId: string, photoId: string) => void;
  setProfilePhoto: (patientId: string, photoId: string) => void;
  
  // Insurance management
  addPatientInsurance: (patientId: string, insurance: Omit<PatientInsurance, 'id'>) => void;
  updatePatientInsurance: (patientId: string, insurance: PatientInsurance) => void;
  deletePatientInsurance: (patientId: string, insuranceId: string) => void;
  uploadInsuranceCard: (patientId: string, insuranceId: string, side: 'front' | 'back', file: File) => Promise<void>;
  
  // Computed
  getAllPatients: () => Patient[];
  getPatient: (patientId: string) => Patient | undefined;
  getFilteredPatients: () => Patient[];
}

export const usePatientStore = create<PatientStore>((set, get) => ({
  patients: [],
  searchQuery: '',
  filterType: 'all',
  editingPatient: null,
  selectedPatient: null,

  setPatients: (patients) => set({ patients }),

  addPatient: (patient) => set((state) => ({
    patients: [...state.patients, patient]
  })),

  updatePatient: (patient) => set((state) => ({
    patients: state.patients.map(p => p.id === patient.id ? patient : p)
  })),

  deletePatient: (patientId) => set((state) => ({
    patients: state.patients.filter(p => p.id !== patientId)
  })),

  setSearchQuery: (query) => set({ searchQuery: query }),

  setFilterType: (type) => set({ filterType: type }),

  setEditingPatient: (patient) => set({ editingPatient: patient }),

  setSelectedPatient: (patient) => set({ selectedPatient: patient }),
  
  uploadPatientPhoto: async (patientId, file) => {
    try {
      // Create a mock photo object for demo purposes
      // In a real implementation, this would upload to a server
      const photo: PatientPhoto = {
        id: `photo-${Date.now()}`,
        url: URL.createObjectURL(file),
        fileName: file.name,
        uploadDate: new Date().toISOString(),
        uploadedBy: 'current-user', // Would get from auth store
        fileSize: file.size,
        mimeType: file.type,
        isProfile: false
      };

      set((state) => ({
        patients: state.patients.map(patient => {
          if (patient.id === patientId) {
            const photos = patient.photos || [];
            const updatedPhotos = [...photos, photo];
            return {
              ...patient,
              photos: updatedPhotos,
              profilePhoto: !patient.profilePhoto ? photo : patient.profilePhoto
            };
          }
          return patient;
        })
      }));
    } catch (_error) {
      throw new Error('Failed to upload photo');
    }
  },

  removePatientPhoto: (patientId, photoId) => {
    set((state) => ({
      patients: state.patients.map(patient => {
        if (patient.id === patientId) {
          const photos = (patient.photos || []).filter(photo => photo.id !== photoId);
          return {
            ...patient,
            photos,
            profilePhoto: patient.profilePhoto?.id === photoId ? undefined : patient.profilePhoto
          };
        }
        return patient;
      })
    }));
  },

  setProfilePhoto: (patientId, photoId) => {
    set((state) => ({
      patients: state.patients.map(patient => {
        if (patient.id === patientId) {
          const photo = patient.photos?.find(p => p.id === photoId);
          if (photo) {
            return {
              ...patient,
              profilePhoto: { ...photo, isProfile: true }
            };
          }
        }
        return patient;
      })
    }));
  },

  addPatientInsurance: (patientId, insurance) => {
    set((state) => ({
      patients: state.patients.map(patient => {
        if (patient.id === patientId) {
          const newInsurance: PatientInsurance = {
            ...insurance,
            id: `insurance-${Date.now()}`
          };
          
          // If this is set as primary, unset other primary insurances
          let insurances = patient.insurances || [];
          if (newInsurance.isPrimary) {
            insurances = insurances.map(ins => ({ ...ins, isPrimary: false }));
          }
          
          return {
            ...patient,
            insurances: [...insurances, newInsurance]
          };
        }
        return patient;
      })
    }));
  },

  updatePatientInsurance: (patientId, updatedInsurance) => {
    set((state) => ({
      patients: state.patients.map(patient => {
        if (patient.id === patientId) {
          let insurances = patient.insurances || [];
          
          // If this is set as primary, unset other primary insurances
          if (updatedInsurance.isPrimary) {
            insurances = insurances.map(ins => 
              ins.id === updatedInsurance.id 
                ? ins 
                : { ...ins, isPrimary: false }
            );
          }
          
          return {
            ...patient,
            insurances: insurances.map(ins => 
              ins.id === updatedInsurance.id ? updatedInsurance : ins
            )
          };
        }
        return patient;
      })
    }));
  },

  deletePatientInsurance: (patientId, insuranceId) => {
    set((state) => ({
      patients: state.patients.map(patient => {
        if (patient.id === patientId) {
          return {
            ...patient,
            insurances: (patient.insurances || []).filter(ins => ins.id !== insuranceId)
          };
        }
        return patient;
      })
    }));
  },

  uploadInsuranceCard: async (patientId, insuranceId, side, file) => {
    try {
      // Create a mock URL for demo purposes
      // In a real implementation, this would upload to a server
      const imageUrl = URL.createObjectURL(file);
      
      set((state) => ({
        patients: state.patients.map(patient => {
          if (patient.id === patientId) {
            return {
              ...patient,
              insurances: (patient.insurances || []).map(insurance => {
                if (insurance.id === insuranceId) {
                  return {
                    ...insurance,
                    cardImages: {
                      ...insurance.cardImages,
                      [side]: imageUrl
                    }
                  };
                }
                return insurance;
              })
            };
          }
          return patient;
        })
      }));
    } catch (_error) {
      throw new Error('Failed to upload insurance card image');
    }
  },

  getAllPatients: () => {
    return get().patients;
  },

  getPatient: (patientId) => {
    return get().patients.find(p => p.id === patientId);
  },

  getFilteredPatients: () => {
    const { patients, searchQuery } = get();
    return patients.filter(patient => {
      const matchesSearch = searchQuery === '' ||
        patient.name.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
        patient.name.ar?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }
}));